import {
  User,
  UsersList,
  UserInviteData,
  UserFormData,
  UserStats,
  UserSummary,
} from '@/types/user';
import { apiClient } from '@/utils/api-client';

class UserService {
  // User management methods
  async getUsers(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    status_filter?: string;
    role_filter?: string;
  }): Promise<UsersList> {
    return apiClient.get<UsersList>('/users', { params });
  }

  async getUserById(id: string): Promise<User> {
    return apiClient.get<User>(`/users/${id}`);
  }

  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/users/me');
  }

  async createUser(userData: UserFormData): Promise<User> {
    return apiClient.post<User>('/users', userData);
  }

  async inviteUser(inviteData: UserInviteData): Promise<{
    id: string;
    email: string;
    full_name: string;
    status: string;
    invited_at: string;
    invited_by: string;
  }> {
    return apiClient.post('/users/invite', inviteData);
  }

  async updateUser(id: string, userData: Partial<UserFormData>): Promise<User> {
    return apiClient.put<User>(`/users/${id}`, userData);
  }

  async assignUserRoles(
    id: string,
    roleIds: string[]
  ): Promise<{ success: boolean }> {
    return apiClient.put(`/users/${id}/roles`, roleIds);
  }

  async activateUser(id: string): Promise<{ success: boolean }> {
    return apiClient.put(`/users/${id}/activate`, {});
  }

  async deactivateUser(id: string): Promise<{ success: boolean }> {
    return apiClient.put(`/users/${id}/deactivate`, {});
  }

  async resendInvitation(id: string): Promise<{ success: boolean }> {
    return apiClient.post(`/users/${id}/resend-invitation`, {});
  }

  async getUserStats(): Promise<UserStats> {
    return apiClient.get<UserStats>('/users/stats');
  }

  async getActiveUsers(search?: string): Promise<UserSummary[]> {
    return apiClient.get<UserSummary[]>('/users/active', {
      params: { search },
    });
  }

  async updatePassword(
    id: string,
    passwordData: {
      current_password: string;
      new_password: string;
      confirm_password: string;
    }
  ): Promise<{ success: boolean }> {
    return apiClient.put(`/users/${id}/password`, passwordData);
  }
}

export const userService = new UserService();
